import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, inject, Injectable, signal } from '@angular/core';

import { ToastContainerComponent } from './toast-container.component';
import { InternalToast, Toast, ToastConfig, ToastId } from './toast.model';

let nextId = 0;

interface ToastTimerState {
  timer: ReturnType<typeof setTimeout> | null;
  startTime: number;
  remainingTime: number;
}

type ToastTimerStateRecord = Record<ToastId, ToastTimerState>;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private overlay = inject(Overlay);

  private overlayRef?: OverlayRef;
  private containerPortal?: ComponentPortal<ToastContainerComponent>;
  private containerInstance?: ToastContainerComponent;

  private readonly toasts = signal<InternalToast[]>([]);
  private readonly queuedToasts = signal<InternalToast[]>([]);
  private displayDelayTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly visibleToasts = computed(() => this.toasts());

  private toastTimers: ToastTimerStateRecord = {};

  private readonly config: ToastConfig = {
    displayDuration: 5000,
    maxVisibleCount: 3,
    displayDelay: 300,
  };

  openDanger(message: string): void {
    this.open({ message, type: 'danger' });
  }

  openWarning(message: string): void {
    this.open({ message, type: 'warning' });
  }

  openInfo(message: string): void {
    this.open({ message, type: 'info' });
  }

  openSuccess(message: string): void {
    this.open({ message, type: 'success' });
  }

  open({ message, type, template }: Toast): void {
    const toast: InternalToast = {
      id: nextId++,
      message,
      template,
      type,
      destroy: () => this.destroy(toast.id),
    };

    if (this.displayDelayTimer || this.toasts().length >= this.config.maxVisibleCount) {
      this.queuedToasts.update((queue) => [...queue, toast]);
      return;
    }

    this.showToast(toast);
  }

  private showToast(toast: InternalToast): void {
    this.toasts.update((toasts) => [...toasts, toast]);

    if (!this.containerInstance) {
      this.attach();
    }

    this.startDisplayTimer(toast.id);
    this.displayDelayTimer = setTimeout(() => {
      this.displayDelayTimer = null;
      this.processNextToast();
    }, this.config.displayDelay);
  }

  private processNextToast(): void {
    if (this.queuedToasts().length === 0 || this.toasts().length >= this.config.maxVisibleCount) {
      return;
    }

    const [nextToast, ...remainingToasts] = this.queuedToasts();
    this.queuedToasts.set(remainingToasts);
    this.showToast(nextToast);
  }

  destroy(id: ToastId): void {
    this.stopDisplayTimer(id);
    this.toasts.update((toasts) => toasts.filter((toast) => toast.id !== id));

    if (!this.displayDelayTimer && this.toasts().length < this.config.maxVisibleCount) {
      this.processNextToast();
    }
  }

  destroyAll(): void {
    this.clearAllTimers();
    this.queuedToasts.set([]);
    this.toasts.set([]);

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
      this.containerInstance = undefined;
      this.containerPortal = undefined;
    }
  }

  pauseAll(): void {
    this.toasts().forEach((toast) => this.pauseDisplayTimer(toast.id));
    if (this.displayDelayTimer) {
      clearTimeout(this.displayDelayTimer);
      this.displayDelayTimer = null;
    }
  }

  unPauseAll(): void {
    this.displayDelayTimer = null;
    this.toasts().forEach((toast) => this.resumeDisplayTimer(toast.id));
    this.processNextToast();
  }

  private stopDisplayTimer(id: ToastId): void {
    const timerState = this.toastTimers[id];
    if (timerState?.timer) {
      clearTimeout(timerState.timer);
      delete this.toastTimers[id];
    }
  }

  private startDisplayTimer(id: ToastId): void {
    this.stopDisplayTimer(id);
    const now = Date.now();
    this.toastTimers[id] = {
      startTime: now,
      remainingTime: this.config.displayDuration,
      timer: setTimeout(() => {
        this.destroy(id);
      }, this.config.displayDuration),
    };
  }

  private pauseDisplayTimer(id: ToastId): void {
    const timerState = this.toastTimers[id];
    if (timerState?.timer) {
      clearTimeout(timerState.timer);
      timerState.timer = null;
      const elapsed = Date.now() - timerState.startTime;
      timerState.remainingTime = Math.max(0, timerState.remainingTime - elapsed);
      timerState.startTime = Date.now();
    }
  }

  private resumeDisplayTimer(id: ToastId): void {
    const timerState = this.toastTimers[id];
    if (timerState && !timerState.timer && timerState.remainingTime > 0) {
      timerState.startTime = Date.now();
      timerState.timer = setTimeout(() => {
        this.destroy(id);
      }, timerState.remainingTime);
    }
  }

  private clearAllTimers(): void {
    if (this.displayDelayTimer) {
      clearTimeout(this.displayDelayTimer);
      this.displayDelayTimer = null;
    }

    Object.keys(this.toastTimers).forEach((id) => {
      this.stopDisplayTimer(Number(id));
    });
    this.toastTimers = {};
  }

  private attach(): void {
    this.overlayRef = this.overlayRef || this.createOverlay();
    this.containerPortal = this.containerPortal || new ComponentPortal(ToastContainerComponent);
    this.containerInstance = this.containerInstance || this.overlayRef.attach(this.containerPortal).instance;
  }

  private createOverlay(): OverlayRef {
    const overlayConfig = new OverlayConfig();
    overlayConfig.positionStrategy = this.overlay.position().global();
    return this.overlay.create(overlayConfig);
  }
}
